import { useEffect, useMemo, useState } from 'react';
import { mascotAnimations, mascotCopy, type MascotAnimationName } from './mascotData';

export interface MascotContextState {
  activeTab?: string;
  recordStatus?: 'idle' | 'connecting' | 'recording' | 'stopping' | 'error';
  hasTranscript?: boolean;
  analysisLoading?: boolean;
  runningCount?: number;
  useKnowledgeBase?: boolean;
  liveInsight?: {
    type?: MascotAnimationName;
    message?: string;
    createdAt?: number;
  } | null;
}

export function useMascotPlayer(context: MascotContextState) {
  const [manualMessage, setManualMessage] = useState('');

  const animationName: MascotAnimationName = useMemo(() => {
    const insightType = context.liveInsight?.type;
    if (insightType && insightType !== 'idle') return insightType;
    if (context.recordStatus === 'error') return 'error';
    if (context.recordStatus === 'connecting' || context.recordStatus === 'stopping') return 'thinking';
    if (context.recordStatus === 'recording') return 'listening';
    if (context.analysisLoading) return 'thinking';
    if ((context.runningCount || 0) > 0) return 'knowledge';
    if (context.hasTranscript) return 'ready';
    return 'idle';
  }, [context.recordStatus, context.analysisLoading, context.runningCount, context.hasTranscript, context.liveInsight]);

  const message = useMemo(() => {
    if (context.liveInsight?.message?.trim()) return context.liveInsight.message.trim();
    if (manualMessage.trim()) return manualMessage.trim();
    if (context.recordStatus === 'recording') return '我正在旁听会议，实时转写时会帮你盯住需求要点。';
    if (context.recordStatus === 'connecting') return '正在连接转写服务，我先准备好。';
    if (context.recordStatus === 'stopping') return '正在保存这段会议内容。';
    if (context.recordStatus === 'error') return '转写连接异常，但页面原有功能不受影响。';
    if (context.analysisLoading) return '我在整理会议纪要，稍等一下。';
    if ((context.runningCount || 0) > 0) return `正在生成 ${context.runningCount} 个产物，我会盯住进度。`;
    if (context.activeTab === 'materials') return '这里放共创资料，生成产物时我会优先引用。';
    if (context.activeTab === 'templates') return '选择产物后再开始生成，最多 3 个并发。';
    if (context.activeTab === 'outputs') return '成果确认页只展示真实生成结果。';
    if (context.hasTranscript) return '已有会议内容，可以继续录音或生成会议纪要。';
    return mascotCopy[animationName];
  }, [manualMessage, context, animationName]);

  useEffect(() => {
    if (!manualMessage) return;
    const timer = window.setTimeout(() => setManualMessage(''), 5200);
    return () => window.clearTimeout(timer);
  }, [manualMessage]);

  return {
    animation: mascotAnimations[animationName],
    animationName,
    message,
    say: setManualMessage,
  };
}
